import React, { useRef, useImperativeHandle, forwardRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
    useMediaQuery,
    Grid,
    Typography,
    Container,
} from '@material-ui/core';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import { CardBase } from '@components/organisms';
import clsx from 'clsx';
import moment from 'moment';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';

const useStyles = makeStyles(theme => ({
    fullContainer: {
        [theme.breakpoints.up('sm')]: {
            overflowY: 'scroll',
            height: 440,
        },
        width: '100%',
    },
    smallContainer: {
        [theme.breakpoints.down('xs')]: {
            display: 'none',
        },
        width: '50%',
        height: 440,
        marginRight: theme.spacing(1),
        overflowY: 'scroll',
    },
    itemContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingBottom: theme.spacing(1),
    },
    spaceContainer: {
        justifyContent: 'space-between',
        display: 'flex',
    },
    hidden: {
        [theme.breakpoints.down('xs')]: {
            display: 'none',
        },
    },
    selectedCard: {
        backgroundColor: theme.palette.primary.selectedCard,
    },
    nonselectedCard: {
        backgroundColor: theme.palette.background.default,
    },
    selectedCardText: {
        color: '#fff',
    },
    invisible: {
        color: 'transparent',
    },
    header: {
        paddingBottom: theme.spacing(2),
        boxShadow: 'none',
        '& .card-base__content': {
            paddingTop: 0,
            paddingBottom: 0,
        }
    },
    headerContainer: {
        paddingBottom: 0,
    },
    sideBorders: {
        // borderRight: "1px groove rgba(255,255,255)",
        // borderLeft: "1px groove rgba(255,255,255)",
    },
    center: {
        display: "flex",
        justifyContent: "center",
    },
    whiteSpace: {
        whiteSpace: "pre-line",
    },
    pointer: {
        cursor: "pointer"
    },
}));

const isStateCert = cert => ["1"].includes(cert.stateCounty);

const isCountyCert = cert => ["2"].includes(cert.stateCounty);

const ListBase = forwardRef(({ isViewingItem, handleOnClick, certificateDetails, currentlyViewing, className, certificateTypes, isLoading }, ref) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });

    const [sortCertsBy, setSortCertsBy] = useState(
        {
            certificateName: true,
            issueDate: false,
            expirationDate: false,
        }
    );
    const [currentlySortedType, setCurrentlySortedType] = useState('certificateName');

    const allCertificateItemRefs = certificateDetails.reduce((acc, value) => {
        acc[value.certificateDispositionID] = React.createRef();
        return acc;
    }, {});

    useImperativeHandle(
        ref,
        () => ({
            getAllCertificateItems: () => allCertificateItemRefs,
        }));

    const printCertnameWithStateCounty = useCallback((cert) => {

        if (!(Array.isArray(certificateTypes) && certificateTypes.length)) return;
        const certInfo = certificateTypes.find(certType => certType.certificateID === cert.certificateID);
        const certName = cert.certificateName;
        let result = certName;
        if (["1"].includes(certInfo?.stateCounty)) result = `${certName}${cert.stateCode ? ` - ${cert.stateCode}` : ``}`;
        else if (["2"].includes(certInfo?.stateCounty)) result = `${certName}${cert.countyName ? ` - ${cert.countyName}` : ``}`;
        return result;
    }, [certificateTypes]);

    const handleToggleSortCerts = (column) => {
        const temp = {};
        for (let key in sortCertsBy) {
            if (key !== column) {
                temp[key] = false;
            } else {
                temp[key] = !sortCertsBy[column];
                setCurrentlySortedType(column);
            }
        }
        setSortCertsBy(temp);
        sortCertsByColumn(column, temp);
    }

    const sortCertsByColumn = (column, sortBy) => {
        certificateDetails.sort((a, b) => ((sortBy[column] ? a[column] : b[column]) > (sortBy[column] ? b[column] : a[column]) ? 1 : -1));
    }

    return (
        <Grid container spacing={isMd ? 4 : 2} flex className={isViewingItem ? classes.smallContainer : null}>
            {
                !isViewingItem && isMd && (
                    <Grid item xs={12}>
                        <Container className={classes.header}>
                            <Container className={clsx(classes.itemContainer, classes.headerContainer)}>
                                <Grid item sm={2} md={1} />
                                <Container className={classes.spaceContainer}>
                                    <Grid item sm={10} md={3}>
                                        <Typography
                                            variant="h6"
                                            color="primary"
                                            onClick={() => handleToggleSortCerts("certificateName")}
                                            className={classes.pointer}
                                        >
                                            Certificate
                                            {currentlySortedType === "certificateName" && (
                                                sortCertsBy["certificateName"] ?
                                                    <ArrowDropUpIcon />
                                                    :
                                                    <ArrowDropDownIcon />
                                            )
                                            }
                                        </Typography>
                                    </Grid>
                                    <Grid item md={4} className={clsx(classes.sideBorders, classes.center)}>
                                        <Typography
                                            variant="h6"
                                            color="primary"
                                            onClick={() => handleToggleSortCerts("issueDate")}
                                            className={classes.pointer}
                                        >
                                            Issue Date
                                            {currentlySortedType === "issueDate" && (
                                                sortCertsBy["issueDate"] ?
                                                    <ArrowDropUpIcon />
                                                    :
                                                    <ArrowDropDownIcon />
                                            )
                                            }
                                        </Typography>
                                    </Grid>
                                    <Grid item md={3}>
                                        <Typography
                                            variant="h6"
                                            color="primary"
                                            onClick={() => handleToggleSortCerts("expirationDate")}
                                            className={classes.pointer}
                                        >
                                            Expiration Date
                                            {currentlySortedType === "expirationDate" && (
                                                sortCertsBy["expirationDate"] ?
                                                    <ArrowDropUpIcon />
                                                    :
                                                    <ArrowDropDownIcon />
                                            )
                                            }
                                        </Typography>
                                    </Grid>
                                    <Grid item md={0.5} />
                                </Container>
                            </Container>
                        </Container>
                    </Grid>
                )
            }
            {certificateDetails.length === 0 && !isLoading && (
                <Container className={classes.center}>
                    <Typography variant='h6'>You currently do not have any credentials to display</Typography>
                </Container>
            )}
            <Grid container spacing={isMd ? 4 : 2} flex className={isViewingItem ? null : classes.fullContainer}>
                {certificateDetails.map((data, index) => (
                    <Grid item xs={12} key={index} ref={allCertificateItemRefs[data.certificateDispositionID]}>
                        <CardBase
                            liftUp
                            onClick={() => handleOnClick(data, index)}
                            className={currentlyViewing === index ? classes.selectedCard : classes.nonselectedCard}
                        >
                            <Container className={classes.itemContainer}>
                                <Grid item sm={2} md={1}>
                                    <FontAwesomeIcon
                                        icon={data.iconName}
                                        size="2x"
                                        className={currentlyViewing === index ? classes.selectedCardText : className[data.disposition]}
                                    />
                                </Grid>
                                <Container className={classes.spaceContainer}>
                                    <Grid item sm={10} md={data.disposition === 'MISSING' ? 10 : 5}>
                                        <Typography
                                            variant="subtitle2"
                                            className={currentlyViewing === index ? clsx(classes.selectedCardText, classes.whiteSpace) : classes.whiteSpace}>
                                            {printCertnameWithStateCounty(data)}
                                        </Typography>
                                    </Grid>

                                    {
                                        !/MISSING/i.test(data.disposition) && !isViewingItem && isMd && (
                                            <Grid md={3}>
                                                <Typography
                                                    variant="subtitle2"
                                                    className={classes.hidden}>{moment(data.issueDate).format('MM/DD/YYYY')}
                                                </Typography>
                                            </Grid>
                                        )
                                    }

                                    {!/MISSING/i.test(data.disposition) && data.isExpiring && data.expirationDate && isMd && (!(data.certificateID === '259' && data.isVaccineDeclined === "1")) ? (
                                        <Grid item md={3}>
                                            <Typography
                                                variant="subtitle2"
                                                className={clsx(
                                                    classes.hidden, currentlyViewing === index ? classes.selectedCardText : null
                                                )}>
                                                {moment(data.expirationDate).format('MM/DD/YYYY')}
                                            </Typography>
                                        </Grid>
                                    ) : <Grid item md={3} />
                                    }

                                </Container>
                            </Container>
                        </CardBase>
                    </Grid>
                ))}
            </Grid>
        </Grid>
    );
});

ListBase.propTypes = {
    /**
     *  External classes
     */
    className: PropTypes.string,
    /**
     *  Flag whether user is viewing details of item
     */
    isViewingItem: PropTypes.bool,
    /**
     *  Toggling flag
     */
    handleOnClick: PropTypes.func,
    /**
     *  Data to be rendered
     */
    data: PropTypes.array,
    /**
     *  Index of currently selected certificate
     */
    currentlyViewing: PropTypes.number,
};

export default ListBase;
